import { RelatedPurchaseOrder, VisibilityBooking } from '@/common/apis/dtos';
import { EmptyContent } from '@/common/components/empty-content/EmptyContent';
import { removeExtraSpace } from '@/common/utilities/display-utilities';
import { Typography } from '@chr/eds-react';
import { ConfigContext } from '@chr/navisphereonline-shared-components';
import { useTranslation } from '@chr/online-shared-ui-localizations';
import { Box, Stack } from '@mui/system';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useContext } from 'react';
import { CustomGrid } from '@/common/components/grid/custom-grid';
import { useAnalytics } from '@/common/hooks/useAnalytics';
import { Link } from '@mui/material';

interface RelatedOrdersViewProps {
  booking: VisibilityBooking;
}

interface GridCellCustomerPOProps {
  purchaseOrderId: number;
  customerPurchaseOrderNumber?: string;
}

type GridColumn = {
  header: string;
  field: string;
};

const Columns = {
  poNumber: { header: 'Purchase Order Number', field: 'customerPurchaseOrderNumber' } as GridColumn,
  status: { header: 'Status', field: 'status' } as GridColumn
};

const GridCellCustomerPO = ({ purchaseOrderId, customerPurchaseOrderNumber }: GridCellCustomerPOProps) => {
  const analytics = useAnalytics();
  const translate = useTranslation();
  const { onlinePurchaseOrderUri } = useContext(ConfigContext) as AppConfig;

  return !purchaseOrderId || purchaseOrderId === 0 ? (
    <>{customerPurchaseOrderNumber ?? '-'}</>
  ) : (
    <Typography variant="body100">
      <Link
        data-testid={`po-link-${purchaseOrderId}`}
        href={`${onlinePurchaseOrderUri}/#/detail/${purchaseOrderId}`}
        aria-label={translate('Click on the link to see more')}
        sx={{ letterSpacing: '0.5px' }}
        onClick={() => analytics.trackEvent(purchaseOrderId.toString(), 'purchaseorder-link', 'click')}
      >
        {customerPurchaseOrderNumber}
      </Link>
    </Typography>
  );
};

export const RelatedPurchaseOrders = ({ booking }: RelatedOrdersViewProps) => {
  const translate = useTranslation();

  const { relatedPurchaseOrders = [] } = booking;

  const hasPOs = (relatedPurchaseOrders?.length ?? 0) > 0;

  const renderEmptyState = () => (
    <Box display="flex" justifyContent={'center'} className="no-print">
      <EmptyContent title="Related Purchase Orders currently unavailable" bodyText="Check again later for updates." />
    </Box>
  );

  const cols: GridColDef[] = [
    {
      field: Columns.poNumber.field,
      headerName: Columns.poNumber.header,
      flex: 1,
      renderCell: (params: GridRenderCellParams<any, RelatedPurchaseOrder>) => {
        const { purchaseOrderId, customerPurchaseOrderNumber } = params.row;

        return (
          <GridCellCustomerPO
            purchaseOrderId={purchaseOrderId}
            customerPurchaseOrderNumber={customerPurchaseOrderNumber}
          />
        );
      }
    },
    {
      field: Columns.status.field,
      headerName: Columns.status.header,
      valueFormatter: (value: string) => removeExtraSpace(value, '-'),
      flex: 1
    }
  ];

  const rows = relatedPurchaseOrders.map((po, index) => ({
    id: index,
    ...po
  }));

  const renderRelatedPosGrid = () => (
    <CustomGrid
      dataTestId="related-pos"
      columns={cols}
      items={rows}
      sortModel={[
        {
          field: 'customerPurchaseOrderNumber',
          sort: 'desc'
        }
      ]}
    />
  );

  return (
    <Stack
      spacing={3}
      padding={1}
      data-testid="related-po-items-tab"
      sx={{
        marginBottom: (theme) => theme.spacing(2)
      }}
    >
      <Stack spacing={1}>
        <Typography variant="display300">{translate('Related Purchase Orders')}</Typography>
        <Typography variant="body100">{translate('A list of purchase orders associated to your booking.')}</Typography>
      </Stack>
      <Box>{hasPOs ? renderRelatedPosGrid() : renderEmptyState()}</Box>
    </Stack>
  );
};
